import { Link } from "react-router-dom";
import { SecondaryButton } from "../../components/general/SecondaryButton.tsx";
import { useState, useEffect } from "react";

interface ErrorStateProps {
    error: string;
}

export function ErrorState({ error }: ErrorStateProps) {
    return (
        <div className="mx-auto max-w-2xl text-center">
            <div>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                     strokeWidth="1.5" stroke="currentColor"
                     className="size-64 text-primary opacity-70 mx-auto">
                    <path strokeLinecap="round" strokeLinejoin="round"
                          d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"/>
                </svg>
            </div>
            <div className="p-2 rounded-md">
                {error}
            </div>
            <SecondaryButton title={'Terug naar selectie'} onClick={() =>
                window.location.replace('/')
            } className="mx-auto mt-8"/>
        </div>
    );
}

interface LoadingStateProps {
    hasJobStarted: boolean;
    progress: number;
}

export function LoadingState({ hasJobStarted, progress }: LoadingStateProps) {
    return (
        <div className="mx-auto max-w-2xl text-center">
            <div className="flex items-center flex-col my-8">
                <div role="status">
                    <svg aria-hidden="true"
                         className="w-32 h-32 text-gray-200 animate-spin dark:text-gray-600 fill-primary"
                         viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                            fill="currentColor"/>
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                            fill="currentFill"/>
                    </svg>
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">De gegevens
                worden geïmporteerd {hasJobStarted && `(${progress || 0} %)`}</p>
            {!hasJobStarted && <LoadingText
                options={['Talking to the bank...', 'Importing bank account details...', 'Setting up your account...']}/>}
            <p className="mt-6 text-lg leading-8 text-gray-600">
                We werken hard aan het ophalen van de gegevens van de bank en het importeren ervan in
                ons systeem, terwijl we ze ook
                analyseren. Dit kan een paar minuten duren</p>
        </div>
    );
}

interface SuccessStateProps {
    totalTransactions: number;
}

export function SuccessState({ totalTransactions }: SuccessStateProps) {
    return (
        <div className="mx-auto max-w-2xl text-center">
            <div className="flex items-center flex-col my-8">
                <div role="status">
                    <div className="flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                             strokeWidth="1.5"
                             stroke="currentColor" className="size-24 text-green-400">
                            <path strokeLinecap="round" strokeLinejoin="round"
                                  d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                        </svg>
                    </div>
                </div>
            </div>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Gegevens
                succesvol geïmporteerd!</p>
            <p className="mt-6 text-lg leading-8 text-gray-600">Het is ons gelukt om alle
                gegevens van de bank te importeren. We hebben {totalTransactions} transacties gevonden.
                Je kunt nu
                sluiten en teruggaan naar het dashboard.</p>
            <div className="mt-8">
                <div className="bg-gray-50 rounded-3xl mx-auto w-xl">
                    <div className="px-4 py-5 sm:p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-base font-semibold leading-2 text-gray-900">Voeg toe
                                    nog een bank rekening</h3>
                                <div className="mt-2 max-w-xl text-sm text-gray-500">
                                    <p>Helemaal gratis, bij ons</p>
                                </div>
                            </div>
                            <div className="mt-5 sm:ml-6 sm:mt-0 sm:flex sm:flex-shrink-0 sm:items-center">
                                <Link to={'/connect-a-bank'}
                                      className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                                    Voeg toe
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex justify-center w-full mt-16">
                <a href="/welcome"
                   className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                    Naar de app &rarr;
                </a>
            </div>
        </div>
    );
}

interface LoadingTextProps {
    options: string[];
}

function LoadingText({ options }: LoadingTextProps) {
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex((old) => (old + 1) % options.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [options.length]);

    return (
        <div className="flex flex-col items-center mt-3">
            <div className="text-center">
                <div className="text-2xl text-gray-900">
                    {options[currentIndex]}
                </div>
            </div>
        </div>
    );
}
